import type { SeatAssignment } from '@/api/seatAssignmentApi';
import { sectionLabelAnchor } from '@/components/seatmap-studio/sections';

export type Point = { x: number; y: number };

export type SeatInfo = {
    id: string;
    x: number;
    y: number;
    zone: string;
    zoneLabel: string;
    row: string;
    rowPrefix: string;
    seatPrefix: string;
    rowId: string;
    number: string;
    accessible: boolean;
    category?: string;
};

export const seatParts = (s: SeatInfo) => ({
    section: s.zoneLabel,
    row: s.row ? `${s.rowPrefix} ${s.row}`.trim() : '',
    seat: `${s.seatPrefix} ${s.number}`.trim(),
});

export const seatTitle = (s: SeatInfo) => {
    const p = seatParts(s);
    return [p.section, p.row, p.seat].filter(Boolean).join(' · ');
};

export type SeatCategory = { name: string; color: string };

export type RowInfo = { id: string; zone: string; label: string; side: 'all' | 'left' | 'right' | 'none'; seatIds: string[]; left: Point; right: Point };

export type SectionLabelInfo = { key: string; text: string; x: number; y: number; rotation: number };

export type ShapeInfo = {
    key: string;
    name: string;
    type: string;
    variant?: string;
    position: Point;
    size: { width: number; height: number };
    rotation: number;
    points?: Point[];
    color?: string;
    strokeColor?: string;
    strokeWidth?: number;
    curveRadius?: number;
    icon?: string;
    showIcon?: boolean;
    gaType?: string;
    capacity?: number;
    fontSize?: number;
    dashed: boolean;
};

export type SeatingLayout = {
    categories: SeatCategory[];
    seats: SeatInfo[];
    rows: RowInfo[];
    zones: { name: string; seatIds: string[] }[];
    sections: SectionLabelInfo[];
    shapes: ShapeInfo[];
    bounds: { x: number; y: number; width: number; height: number };
};

export type SeatState =
    | { kind: 'open' }
    | { kind: 'assigned'; ticketId: string }
    | { kind: 'hold'; ticketId?: string; note?: string }
    | { kind: 'sold'; ticketId?: string; buyer?: SeatAssignment['orderInfo'] };

export const SEAT_RADIUS = 10;

export const DEFAULT_SEAT_COLOR = '#94A3B8';

export const seatIdOf = (zone: any, row: any, seat: any): string =>
    seat.uuid || seat.seat_guid || `${zone.name}-${row.row_number}-${seat.seat_number}`;

export const buildLayout = (plan: any): SeatingLayout => {
    const seats: SeatInfo[] = [];
    const rows: RowInfo[] = [];
    const zones: SeatingLayout['zones'] = [];
    const sections: SectionLabelInfo[] = [];
    const pts: Point[] = [];

    (plan?.zones ?? []).forEach((zone: any, zi: number) => {
        const zoneSeatIds: string[] = [];
        const zx = zone.position?.x ?? 0;
        const zy = zone.position?.y ?? 0;
        const labels = zone.section ? zone.labels ?? {} : {};
        const zoneName = String(zone.name ?? '');
        const zoneLabel = zone.section ? `${labels.sectionPrefix ?? 'Section'} ${zoneName}`.trim() : zoneName;
        const rowPrefix = String(labels.rowPrefix ?? 'Row');
        const seatPrefix = String(labels.seatPrefix ?? 'Seat');
        const side: RowInfo['side'] = ['left', 'right', 'none'].includes(labels.showRows) ? labels.showRows : 'all';
        (zone.rows ?? []).forEach((row: any, ri: number) => {
            const rx = zx + (row.position?.x ?? 0);
            const ry = zy + (row.position?.y ?? 0);
            const rowId = `${zi}-${ri}`;
            const rowSeats: SeatInfo[] = (row.seats ?? []).map((s: any) => ({
                id: seatIdOf(zone, row, s),
                x: rx + (s.position?.x ?? 0),
                y: ry + (s.position?.y ?? 0),
                zone: zoneName,
                zoneLabel,
                row: row.row_number ?? '',
                rowPrefix,
                seatPrefix,
                rowId,
                number: s.seat_number ?? '',
                accessible: !!s.accessible,
                category: s.category || undefined,
            }));
            if (!rowSeats.length) return;
            seats.push(...rowSeats);
            zoneSeatIds.push(...rowSeats.map(s => s.id));
            pts.push(...rowSeats);
            const first = rowSeats[0];
            const last = rowSeats[rowSeats.length - 1];
            const a = rowSeats.length > 1 ? Math.atan2(rowSeats[1].y - first.y, rowSeats[1].x - first.x) : 0;
            const off = { x: Math.cos(a) * 24, y: Math.sin(a) * 24 };
            rows.push({
                id: rowId,
                zone: zone.name ?? '',
                label: row.tableId ? '' : row.row_number ?? '',
                side,
                seatIds: rowSeats.map(s => s.id),
                left: { x: first.x - off.x, y: first.y - off.y },
                right: { x: last.x + off.x, y: last.y + off.y },
            });
        });
        if (zoneSeatIds.length) zones.push({ name: zone.name ?? `Zone ${zi + 1}`, seatIds: zoneSeatIds });
        if (zone.section && labels.showSection !== false && zoneSeatIds.length) {
            const anchor = sectionLabelAnchor(zone);
            if (anchor) {
                sections.push({ key: `section-${zi}`, text: zoneLabel, x: anchor.point.x, y: anchor.point.y, rotation: anchor.rotation });
                pts.push(anchor.point);
            }
        }
    });

    const shapes: ShapeInfo[] = [
        ...(plan?.stages ?? []).map((s: any, i: number) => ({ ...s, key: `stage-${s.id ?? i}`, dashed: false })),
        ...(plan?.entrances ?? []).map((s: any, i: number) => ({ ...s, key: `entrance-${s.id ?? i}`, dashed: true })),
    ].map((s: any) => ({
        key: s.key,
        name: s.name ?? '',
        type: s.type,
        variant: s.variant,
        position: s.position ?? { x: 0, y: 0 },
        size: s.size ?? { width: 0, height: 0 },
        rotation: s.rotation ?? 0,
        points: s.points,
        color: s.color,
        strokeColor: s.strokeColor,
        strokeWidth: s.strokeWidth,
        curveRadius: s.curveRadius,
        icon: s.icon,
        showIcon: s.showIcon,
        gaType: s.gaType,
        capacity: s.capacity,
        fontSize: s.fontSize,
        dashed: s.dashed,
    }));
    shapes.forEach(s => {
        pts.push(s.position, { x: s.position.x + s.size.width, y: s.position.y + s.size.height });
    });

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    pts.forEach(p => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    });
    const pad = 40;
    const bounds = pts.length
        ? { x: minX - pad, y: minY - pad, width: maxX - minX + pad * 2, height: maxY - minY + pad * 2 }
        : { x: 0, y: 0, width: 400, height: 300 };

    const categories: SeatCategory[] = (plan?.categories ?? [])
        .filter((c: any) => c?.name && c?.color)
        .map((c: any) => ({ name: String(c.name), color: String(c.color) }));

    return { categories, seats, rows, zones, sections, shapes, bounds };
};

export const readableTextOn = (hex: string): string => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return '#FFFFFF';
    const n = parseInt(m[1], 16);
    const luminance = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
    return luminance > 0.62 ? '#1F2937' : '#FFFFFF';
};

export const seatCategoryColor = (layout: SeatingLayout, seat?: SeatInfo): string => {
    if (!seat?.category) return DEFAULT_SEAT_COLOR;
    return layout.categories.find(c => c.name === seat.category)?.color ?? DEFAULT_SEAT_COLOR;
};

export const seatStates = (assignments: SeatAssignment[]): Map<string, SeatState> => {
    const map = new Map<string, SeatState>();
    assignments.forEach(a => {
        const ticketId = a.ticketId ? String(a.ticketId) : undefined;
        if (a.status === 'reserved') {
            map.set(a.seatUuid, { kind: 'sold', ticketId, buyer: a.orderInfo });
        } else if (a.status === 'hold') {
            map.set(a.seatUuid, { kind: 'hold', ticketId, note: a.holdNote });
        } else if (ticketId) {
            map.set(a.seatUuid, { kind: 'assigned', ticketId });
        }
    });
    return map;
};
